import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';

import { weightSchema } from '../../../utils/schemas';
import { toggleModal } from '../../../features/ui/uiSlice';

//Components
import { TextAnswerList, TextInput, HeightInput } from '../../InputComponents';
import { Modal } from '../..';
import NeedsRetest from './NeedsRetest';
import PriorStudyModalContent from '../PriorStudyModalContent';
import { AnswerGroup, ChecklistQuestion } from './QuestionTypes';
import { useAssessmentContext } from '../../../providers/AssessmentContext';

function Question() {
    const dispatch = useDispatch();

    const {
        answers,
        currentQuestionData,
        currentQuestionIndex,
        nextQuestion,
        previouslyDiagnosed,
        questionChoices,
        questions,
        setAnswer,
    } = useAssessmentContext();

    const [questionModalOpen, setQuestionModalOpen] = useState(false);

    function inputSubmitHandler(val) {
        const currentAnswerKey = questions[currentQuestionIndex].answerKey;

        if (currentAnswerKey === 'howBroughtToGem' && val === 'OTHER') {
            dispatch(toggleModal('noReasonListed'));
            return;
        }

        setAnswer({ answer: val, answerKey: currentAnswerKey });

        nextQuestion(currentQuestionData?.answerKey);
    }

    function getSubhead() {
        if (previouslyDiagnosed && currentQuestionData?.subheadPreviouslyDiagnosed) {
            return answers?.howBroughtToGem === 'NEED_NEW_SLEEP_TEST'
                ? currentQuestionData?.subhead || ''
                : currentQuestionData?.subheadPreviouslyDiagnosed;
        } else {
            return currentQuestionData?.subhead;
        }
    }

    function getTitle() {
        if (currentQuestionData?.titlePreviouslyDiagnosed || currentQuestionData?.title) {
            if (previouslyDiagnosed && currentQuestionData?.titlePreviouslyDiagnosed) {
                return currentQuestionData?.titlePreviouslyDiagnosed;
            }

            return currentQuestionData?.title;
        }
    }

    function heightWeightHandler(answer) {
        const currentAnswerKey = questions[currentQuestionIndex].answerKey;

        setAnswer({
            answer:
                currentAnswerKey === 'heightInches' || currentAnswerKey === 'weightLbs'
                    ? +answer
                    : answer,
            answerKey: currentAnswerKey,
        });

        nextQuestion(currentQuestionData?.answerKey);
    }

    // conditions for rendering stuff other than questions
    if (currentQuestionData?.answerKey === 'situationChanged') {
        return <NeedsRetest sleepStudyInvalidated={true} />;
    }

    if (currentQuestionData?.answerKey === 'needNewSleepStudy') {
        return <NeedsRetest sleepStudyInvalidated={false} />;
    }

    const isPriorStudyQuestion = currentQuestionData?.answerKey === 'hasPriorStudy';

    const { linkText, modalContent, titleText } = PriorStudyModalContent();

    return (
        <>
            <AnimatePresence mode="wait">
                <motion.div
                    key={`question-div-${currentQuestionIndex}`}
                    initial={{ opacity: 0, translateX: '20%' }}
                    animate={{ opacity: 1, translateX: 0 }}
                    exit={{ opacity: 0, translateX: '-20%' }}
                    transition={{ ease: 'linear', duration: 0.3 }}
                    className="mx-auto max-w-4xl pb-8">
                    <header
                        className={`text-center ${
                            currentQuestionData?.content ||
                            currentQuestionData?.type === 'checklist'
                                ? 'mb-5 xs:mb-10'
                                : 'mb-20'
                        }`}>
                        {(currentQuestionData?.subhead ||
                            currentQuestionData?.subheadPreviouslyDiagnosed) && (
                            <p className="mb-4 font-heading text-2xl xs:text-4xl lg:text-6xl">
                                {getSubhead()}
                            </p>
                        )}
                        <div
                            className={`${currentQuestionData?.image && 'grid grid-cols-[1fr_90px] lg:grid-cols[1fr_200px] items-center'}`}>
                            <h2
                                className={`font-heading mb-2 text-2xl ${['highRiskSelections', 'highRiskSelections2'].includes(currentQuestionData?.answerKey) || currentQuestionData?.image ? 'xs:text-3xl' : 'xs:text-5xl'} lg:text-6xl`}>
                                {isPriorStudyQuestion ? titleText : getTitle()}
                            </h2>

                            {currentQuestionData?.image && (
                                <img
                                    src={currentQuestionData?.image}
                                    alt=""
                                    className="max-w-[70px] max-h-[150px] lg:max-w-[190px] lg:max-h-[200px] ml-[15px] object-cover object-center"
                                />
                            )}
                        </div>

                        {currentQuestionData?.underhead && (
                            <p className="lg:text-[21px] my-4">{currentQuestionData?.underhead}</p>
                        )}

                        {currentQuestionData?.modalLabel && (
                            <button
                                onClick={() => {
                                    setQuestionModalOpen(true);
                                }}
                                className="border-b-2 border-green-400 font-semibold text-white/60 transition hover:text-white text-xs sm:text-base">
                                {isPriorStudyQuestion ? linkText : currentQuestionData?.modalLabel}
                            </button>
                        )}
                    </header>

                    {currentQuestionData?.type === 'input' && (
                        <TextInput
                            defaultValue={answers[currentQuestionData?.answerKey] || ''}
                            placeholder={currentQuestionData?.placeholder}
                            submitHandler={inputSubmitHandler}
                        />
                    )}

                    {currentQuestionData?.content && (
                        <div className="mb-28 text-center">{currentQuestionData?.content}</div>
                    )}

                    {currentQuestionData?.type === 'height' && (
                        <HeightInput submitHandler={heightWeightHandler} />
                    )}

                    {currentQuestionData?.type === 'weight' && (
                        <TextInput
                            defaultValue={answers[currentQuestionData?.answerKey] || ''}
                            placeholder="LBS"
                            validationSchema={weightSchema}
                            maxLength={3}
                            inputMode="numeric"
                            submitHandler={heightWeightHandler}
                        />
                    )}

                    {currentQuestionData?.type === 'list' && (
                        <TextAnswerList
                            choices={questionChoices}
                            choiceHandler={inputSubmitHandler}
                            multiple={currentQuestionData?.multiple}
                            uniqueChoice={currentQuestionData?.uniqueChoice}
                        />
                    )}

                    {currentQuestionData?.type === 'checklist' && (
                        <ChecklistQuestion inputSubmitHandler={inputSubmitHandler} />
                    )}

                    {currentQuestionData?.type === 'yesno' && <AnswerGroup />}

                    {currentQuestionData?.skipText && (
                        <button
                            key={currentQuestionData?.answerKey + '-skip'}
                            onClick={() => {
                                nextQuestion(currentQuestionData?.answerKey);
                            }}
                            className={`mx-auto mt-10 block w-auto text-center ${
                                currentQuestionData?.skipAsButton
                                    ? 'btn-primary'
                                    : 'border-b-2 border-green-400 font-semibold text-white/60 transition hover:text-white text-xs sm:text-base'
                            }`}>
                            {currentQuestionData?.skipText}
                        </button>
                    )}
                </motion.div>
            </AnimatePresence>

            {currentQuestionData?.modalLabel && (
                <Modal
                    key={currentQuestionData?.answerKey + '-modal'}
                    showClose={true}
                    isOpen={questionModalOpen}
                    closeHandler={() => {
                        setQuestionModalOpen(false);
                    }}>
                    {isPriorStudyQuestion ? modalContent : currentQuestionData?.modalContent}
                </Modal>
            )}
        </>
    );
}

export default Question;
